import { FontColorEnum, FormField, FormHint, InputFile } from '@acceso/design-system';
import { ChangeEvent } from 'react';
import { Controller, FieldValues, Path, PathValue, UseFormReturn } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import { isFileAccepted } from '@/utils/files';

type TInputFilesProps<T extends FieldValues> = {
  form: UseFormReturn<T>;
  name: Path<T>;
  label: string;
  isImage: boolean;
  required?: boolean;
  disabled?: boolean;
  placeholder?: string;
  accept?: string;
  defaultValue?: PathValue<T, Path<T>> | undefined;

  /**
   * @default false
   */
  singleFile?: boolean;
};

export function InputFiles<T extends FieldValues>({
  form: { control },
  label,
  name,
  isImage,
  required,
  disabled,
  placeholder,
  accept,
  defaultValue,
  singleFile = false,
}: TInputFilesProps<T>) {
  const { t } = useTranslation();

  return (
    <FormField className="!flex-row flex-wrap gap-size-24">
      <Controller
        disabled={disabled}
        control={control}
        name={name}
        defaultValue={defaultValue}
        rules={{
          required: required ? { value: true, message: t('El campo es obligatorio') } : false,
        }}
        render={({ field: { onChange, value }, fieldState: { error } }) => {
          const handleChange = (e: ChangeEvent<HTMLInputElement> | null) => {
            const fileList = e?.target.files ?? null;

            if (!fileList) {
              onChange(fileList);
              return;
            }

            const accepted = Array.from(fileList).filter(f => isFileAccepted(f, accept));

            if (singleFile) {
              onChange(accepted.length > 0 ? accepted[0] : null);
            } else {
              onChange(accepted as unknown as FileList);
            }
          };
          return (
            <div className="flex flex-col gap-size-8">
              <InputFile
                label={label}
                placeholder={placeholder || label}
                url={value}
                required={required}
                onChange={handleChange}
                accept={accept}
                isImage={isImage}
                disabled={disabled}
                hasError={!!error}
              />
              {error && <FormHint color={FontColorEnum.Error20}>{error.message}</FormHint>}
            </div>
          );
        }}
      />
    </FormField>
  );
}
